import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { type NavigateOptions, useNavigate, useRouter } from '@tanstack/react-router';
import { type MouseEvent, useState } from 'react';
import { cn } from '../lib/utils.ts';
import { Empty } from './ui/states.tsx';

/**
 * shadcn data-table 모양 — TanStack Table 에 정렬만. 페이지네이션은 v1 규모에서 필요 없다.
 *
 * 행 클릭은 두 가지다.
 *  - `rowLink` — **다른 화면으로 가는** 행. Ctrl/⌘·Shift+클릭과 휠 클릭은 새 탭으로, 그냥 클릭은 같은 탭으로 간다.
 *    행이 진짜 `<a>` 가 아니라 `<tr>` 이라 브라우저가 그 처리를 안 해 주므로 여기서 한다(우클릭 메뉴는 없다 — 알고 둔 것)
 *  - `onRowClick` — 화면 안의 상태만 바꾸는 행(패널 열기·zone 고르기). 새 탭이 뜻이 없다
 * 셀 안의 버튼·링크는 각자 `stopPropagation` 으로 행 클릭과 갈라 둔다.
 */
export function DataTable<T>({
  columns,
  data,
  empty = '없습니다',
  onRowClick,
  rowLink,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: ColumnDef 의 값 타입은 열마다 달라 한 배열에 담으려면 any 가 필요하다
  columns: ColumnDef<T, any>[];
  data: T[];
  empty?: string;
  onRowClick?: (row: T) => void;
  /** 행이 가리키는 화면 — 주면 `onRowClick` 보다 우선한다 */
  rowLink?: (row: T) => NavigateOptions;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const router = useRouter();
  const navigate = useNavigate();
  const openNewTab = (to: NavigateOptions) => window.open(router.buildLocation(to).href, '_blank', 'noopener');
  const handleClick = (row: T, e: MouseEvent) => {
    if (rowLink) {
      const to = rowLink(row);
      if (e.ctrlKey || e.metaKey || e.shiftKey) openNewTab(to);
      else void navigate(to);
    } else onRowClick?.(row);
  };
  // 휠 클릭 — click 이벤트가 아니라 auxclick 으로 온다
  const handleAuxClick = (row: T, e: MouseEvent) => {
    if (rowLink && e.button === 1) openNewTab(rowLink(row));
  };
  const clickable = Boolean(rowLink || onRowClick);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  if (data.length === 0) return <Empty>{empty}</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr
              key={hg.id}
              className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500"
            >
              {hg.headers.map((h) => (
                <th key={h.id} className="whitespace-nowrap px-3 py-2 font-medium">
                  {h.isPlaceholder ? null : (
                    <button
                      type="button"
                      className={cn(h.column.getCanSort() && 'cursor-pointer select-none')}
                      onClick={h.column.getToggleSortingHandler()}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {{ asc: ' ↑', desc: ' ↓' }[h.column.getIsSorted() as string] ?? ''}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={cn('border-b border-slate-100 last:border-0', clickable && 'cursor-pointer hover:bg-slate-50')}
              onClick={clickable ? (e) => handleClick(row.original, e) : undefined}
              onAuxClick={rowLink ? (e) => handleAuxClick(row.original, e) : undefined}
              // 휠 버튼을 누르는 순간 자동 스크롤 모드로 들어가지 않게 — 링크 위에서와 같은 동작이다
              onMouseDown={rowLink ? (e) => e.button === 1 && e.preventDefault() : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-2 align-top">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
